import React from 'react'
import { head, map, pluck, sortBy, transpose, zip, zipObj } from 'ramda'
import { multiSelectWidget, singleSelectWidget, paymentWidget, vatIdStatus, TermsAndConditionsText } from './formWidgets'
import { getTermsList, validSteps } from './index'

export const getSchema = (intl, countries, motherTongues, sourceLanguages, workAreas, step, isVatIdUnverified) => {
  const mapEnumNames = (prefix) => map(value => intl.formatMessage({id: prefix ? `${prefix}.${value}` : value}))
  const termsList = getTermsList(intl)
  const { personal, languages, terms, company } = validSteps

  const labelAndSort = (prefix, enums) =>
    zipObj(['labels', 'values'],
      transpose(
        sortBy(head, zip(
          mapEnumNames(prefix)(enums),
          enums
          )
        )
      )
    )

  const motherTongueEntries = labelAndSort('language', motherTongues)
  const sourceLanguageEntries = labelAndSort('language', sourceLanguages)

  let schema
  switch (step) {
    case languages:
      schema = {
        type: 'object',
        properties: {
          motherTongue: {
            type: 'string',
            title: 'Native language',
            enum: motherTongueEntries.values,
            enumNames: motherTongueEntries.labels
          },
          languageKnowledges: {
            type: 'array',
            title: 'Language and industry knowledge',
            description: <span><p className="no-danger body-text">
              This is where you add the languages you translate from. <br/>
              For some jobs, specific industry knowledge is required. <br/>
              We’ll automatically invite you to bid on all jobs that match your profile.</p>
              <div className="form-error-message">Add at least one source language</div>
            </span>,

            items: {
              type: 'object',
              required: [
                'sourceLanguage',
                'workArea'
              ],
              properties: {
                sourceLanguage: {
                  type: 'string',
                  title: 'Source language',
                  enum: sourceLanguageEntries.values,
                  enumNames: sourceLanguageEntries.labels
                },
                workArea: {
                  type: 'array',
                  title: 'Work Area',
                  items: {
                    type: 'string',
                    enum: pluck('id', workAreas),
                    enumNames: mapEnumNames()(pluck('descriptionKey', workAreas))
                  },
                  uniqueItems: true
                }
              }
            }
          }
        },
        required: ['motherTongue', 'languageKnowledges']
      }
      break
    case personal:
      schema = {
        type: 'object',
        properties: {
          firstName: {type: 'string', title: 'First Name'},
          lastName: {type: 'string', title: 'Last Name'},
          email: {type: 'string', title: 'Email'},
          emailVerified: {type: 'boolean', title: 'Verified'},
          phone: {type: 'string', title: 'Phone'}
        },
        required: ['firstName', 'lastName']
      }
      break
    case company:
      schema = {
        type: 'object',
        properties: {
          country: {
            type: 'string',
            title: 'Country',
            enum: countries,
            enumNames: mapEnumNames('country')(countries)
          },
          vatId: {type: 'string', title: 'Vat Id'},
          vatIdStatus: {type: 'boolean', title: 'Status'},
          companyName: {type: 'string', title: 'Business Name'},
          address1: {type: 'string', title: 'Address'},
          address2: {type: 'string', title: 'Address'},
          area: {type: 'string', title: 'Area / State / Province'},
          zip: {type: 'string', title: 'Zip'},
          city: {type: 'string', title: 'City'},
          stripeAccountId: {type: 'string', title: 'Payment'}
        },
        required: ['country', 'vatId', 'companyName', 'address1', 'zip', 'city']
      }
      break
    case terms:
      schema = {
        type: 'object',
        properties: {
          terms: {
            type: 'array',
            title: 'Terms & Conditions',
            items: {
              type: 'string',
              enum: termsList
            },
            minItems: termsList.length,
            uniqueItems: true
          },
          descriptionOfTerms: {
            title: 'Learn more here',
            type: 'string'
          }
        },
        required: ['terms']
      }
      break
  }

  return schema
}
export const uiSchema = (step, isVatIdUnverified, isVatIdInvalid, loading, isProfileActive, canUseStripe, intl) => {
  const { personal, languages, terms, company } = validSteps

  return {
    'ui:field': 'layout_grid',
    'ui:layout_grid': {
      'ui:row': [
        {
          'ui:col': {
            md: 12,
            children: [
              {
                'ui:group': step === languages ? 'Language skills' : null,
                'ui:row': [
                  {'ui:col': {md: 12, children: ['motherTongue']}},
                  {'ui:col': {md: 12, children: ['languageKnowledges']}}
                ]
              },
              {
                'ui:group': step === personal ? 'Personal' : null,
                'ui:row': [
                  {'ui:col': {md: 6, children: ['firstName']}},
                  {'ui:col': {md: 6, children: ['lastName']}},
                  {'ui:col': {md: 6, children: ['email']}},
                  {'ui:col': {md: 6, children: ['phone']}}
                ]
              },
              {
                'ui:group': step === company ? 'Company' : null,
                'ui:row': [
                  {'ui:col': {md: 7, children: ['country']}},
                  {'ui:col': {md: 5, children: ['vatId']}},
                  {'ui:col': {md: 12, children: ['companyName']}},
                  {'ui:col': {md: 12, children: ['address1']}},
                  {'ui:col': {md: 12, children: ['address2']}},
                  {'ui:col': {md: 3, children: ['zip']}},
                  {'ui:col': {md: 9, children: ['city']}},
                  {'ui:col': {md: 6, children: ['area']}},
                  {'ui:col': {md: 6, children: ['stripeAccountId']}}
                ]
              },
              {
                'ui:group': step === terms ? 'Terms & Conditions' : null,
                'ui:row': [
                  {'ui:col': {md: 9, children: ['terms']}},
                  {'ui:col': {md: 12, children: ['descriptionOfTerms']}}
                ]
              }
            ]
          }
        }
      ]
    },
    motherTongue: {
      'ui:widget': singleSelectWidget,
      'ui:placeholder': intl.formatMessage({id: 'component.specialistDetails.placeholder.motherTongue'}),
      'ui:options': {
        disabled: isProfileActive
      }
    },
    languageKnowledges: {
      'ui:options': {
        'orderable': false
      },
      items: {
        sourceLanguage: {
          'ui:widget': singleSelectWidget,
          'ui:placeholder': intl.formatMessage({id: 'component.specialistDetails.placeholder.sourceLanguage'}),
        },
        workArea: {
          'ui:widget': multiSelectWidget,
          'ui:placeholder': intl.formatMessage({id: 'component.specialistDetails.placeholder.workArea'}),
        }
      }
    },
    email: {
      'ui:disabled': true,
      'ui:options': {
        'inputType': 'email'
      }
    },
    emailVerified: {
      'ui:widget': (props) => <i
        className={'zmdi zmdi-shield-check email-verified' + (props.value ? ' text-success' : ' text-grey')}/>
    },
    phone: {
      'ui:options': {
        'inputType': 'tel'
      }
    },
    country: {
      'ui:widget': singleSelectWidget,
      'ui:options': {
        disabled: !isVatIdUnverified,
        hideErrorMessage: true
      }
    },
    vatId: {
      'ui:disabled': !isVatIdUnverified,
      'ui:placeholder':  'Please enter your Vat Id without country prefix',
      'ui:help': isVatIdInvalid ? <span className="text-danger icons-wrapper">Not valid</span> : undefined
    },
    companyName: {
      'ui:placeholder':  'This will be our reference on your payment statements'
    },
    stripeAccountId: {
      'ui:widget': paymentWidget,
      'ui:options': {
        canUseStripe
      }
    },
    descriptionOfTerms: {
      'ui:widget': TermsAndConditionsText
    },
    terms: {
      'ui:widget': 'checkboxes'
    }
  }
}



// WEBPACK FOOTER //
// ./Linkgua/components/SpecialistDetails/formConfiguration.js